import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UilExclamationCircle, UilInbox, UilLocationArrow, UilPen, UilTimes, } from '@iconscout/react-unicons';
import { UilStar, UilTrash, UilShare, UilUsersAlt } from '@iconscout/react-unicons';
import classNames from 'classnames';
import Button from 'components/base/Button';
import {
  SidebarItem,
  filteredItems,
  labelItems,
  mailboxItems
} from 'data/email';
import React, { useEffect, useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { MyResource } from './DataDefinitions';




const ResourcesSidebarItem = ({ item }: { item: SidebarItem }) => {
  return (
    <Nav.Item>
      <Nav.Link
        as={Link}
        className={classNames(
          'py-2 ps-0 pe-3 border-end border-bottom text-start outline-none',
          {
            active: item.active
          }
        )}
        to={item.link ? item.link : '#!'}
      >
        <div className="d-flex gap-2 align-items-center">
          {item.icon}
          <span className="flex-1">{item.label}</span>
          {item.count && <span className="nav-item-count fs-9">{item.count}</span>}
        </div>
      </Nav.Link>
    </Nav.Item>
  );
};

const ResourcesSidebar: React.FC<{ resources: MyResource[], hideSidebar?: () => void }> = ({ hideSidebar, resources }) => {

  const [sidebarItems, setSidebarItems] = useState<SidebarItem[]>([]);

  // Compute the sidebar item for all resources
  const allResourcesItem = useMemo(() => ({
    icon: <UilInbox size={12} />,
    label: 'All Resources',
    count: resources.length,
    link: '/apps/email/inbox'
  }), [resources]);

  // Compute the sidebar item for "Shared By" (count resources with sharedBy)
  const sharedByItem = useMemo(() => {
    const count = resources.filter(res => res.shared_by).length;
    return count > 0
      ? {
        icon: <UilShare size={12} />,
        label: 'Shared By Me',
        count,
        link: '/apps/email/shared-by'
      }
      : null;
  }, [resources]);

  // Compute the sidebar item for "Shared With" (count resources with sharedWith)
  const sharedWithItem = useMemo(() => {
    const count = resources.filter(res => res.shared_with).length;
    return count > 0
      ? {
        icon: <UilUsersAlt size={12} />,
        label: 'Shared With Me',
        count,
        link: '/apps/email/shared-with'
      }
      : null;
  }, [resources]);

  // Compute the sidebar item for deleted resources
  const deletedResourcesItem = useMemo(() => ({
    icon: <UilTrash size={12} />,
    label: 'Deleted Resources',
    count: resources.filter(res => res.deleted).length,
    link: '/apps/email/trash'
  }), [resources]);

  useEffect(() => {
    // Build sidebar items list dynamically, filtering out null values
    setSidebarItems([
      allResourcesItem,
      //favoriteResourcesItem,
      deletedResourcesItem,
      sharedByItem,
      sharedWithItem
    ].filter(Boolean) as SidebarItem[]);
  }, [allResourcesItem /*favoriteResourcesItem*/, deletedResourcesItem, sharedByItem, sharedWithItem]);



  return (
    <div>
      <div className="email-content scrollbar">
        <div className="d-flex flex-between-center mb-2">
          <p className="text-uppercase fs-10 text-600 mb-0 fw-bold">Files</p>
          {hideSidebar && (
            <Button
              className="d-lg-none p-0 mb-1"
              onClick={() => hideSidebar()}
            >
              <UilTimes size={16} />
            </Button>
          )}
        </div>

        <Nav className="flex-column border-top fs-9 vertical-nav mb-4">
          {sidebarItems.map(item => (
            <ResourcesSidebarItem item={item} key={item.label} />
          ))}
        </Nav>

        <div className="d-flex flex-between-center mb-2">
          <p className="text-uppercase fs-10 text-600 mb-0 fw-bold">Filtered</p>
          <Button
            variant="link"
            className="fs-10 fw-bold p-0"
            startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
          >
            Add Folder
          </Button>
        </div>

        <Nav className="flex-column border-top fs-9 vertical-nav mb-4">
          {filteredItems.map(item => (
            <ResourcesSidebarItem item={item} key={item.label} />
          ))}
        </Nav>

        <div className="d-flex flex-between-center mb-2">
          <p className="text-uppercase fs-10 text-600 mb-0 fw-bold">Labels</p>
          <Button
            variant="link"
            className="fs-10 fw-bold p-0"
            startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
          >
            Add Label
          </Button>
        </div>

        <Nav className="flex-column border-top fs-9 vertical-nav">
          {labelItems.map(item => (
            <ResourcesSidebarItem item={item} key={item.label} />
          ))}
        </Nav>
      </div>
    </div>
  );
};

export default ResourcesSidebar;
